import { c as _c } from "react/compiler-runtime";
import { isEmpty } from '@newfront-insurance/core';
import { Button, cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@newfront-insurance/core-ui/v2';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { AnimatePresence, motion } from 'framer-motion';
import { FileText, X, Loader2, AlertCircle } from 'lucide-react';
import { useCallback, type Dispatch, type SetStateAction } from 'react';
import type { FileAttachment } from '../../hooks/use-file-selection';
export function InputAttachmentsPreview(t0) {
  const $ = _c(15);
  const {
    attachments,
    setAttachments,
    className
  } = t0;
  let t1;
  if ($[0] !== attachments || $[1] !== setAttachments) {
    t1 = tempId => {
      const attachmentToRemove = attachments.find(a => a.tempId === tempId);
      if (attachmentToRemove) {
        browserLogger.info("[Nova] Removing attachment", {
          fileName: attachmentToRemove.file.name,
          tempId,
          wasUploading: attachmentToRemove.isUploading,
          hadError: !!attachmentToRemove.uploadError,
          fileUuid: attachmentToRemove.fileUuid
        });
      }
      setAttachments(prev => prev.filter(attachment => attachment.tempId !== tempId));
    };
    $[0] = attachments;
    $[1] = setAttachments;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleRemoveAttachment = t1;
  if (isEmpty(attachments)) {
    return null;
  }
  let t2;
  if ($[3] !== className) {
    t2 = cn("bg-steel-50 flex w-full flex-row gap-2 p-3", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== attachments || $[6] !== handleRemoveAttachment) {
    let t4;
    if ($[8] !== handleRemoveAttachment) {
      t4 = attachment_0 => <motion.div key={attachment_0.tempId} initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.2,
        ease: "easeInOut"
      }} className={cn("bg-background flex items-center justify-between rounded-md border", "size-[70px] overflow-hidden shadow-xs transition-colors", "hover:border-rebrand-400 hover:bg-muted/50 relative", {
        "border-yellow-400": attachment_0.isUploading,
        "border-red-400": attachment_0.uploadError,
        "border-green-400": attachment_0.fileUuid && !attachment_0.isUploading
      })}><FilePreview attachment={attachment_0} />{attachment_0.isUploading && <div className="bg-background/80 absolute inset-0 flex items-center justify-center"><Loader2 className="size-4 animate-spin text-yellow-600" /></div>}{attachment_0.uploadError && <div className="bg-background/80 absolute inset-0 flex items-center justify-center"><AlertCircle className="size-4 text-red-600" /></div>}<Button variant="transparent" size="small" className={cn("bg-steel-100/75 absolute top-0.5 right-0.5 shrink-0 rounded-full", "hover:bg-rebrand-400 h-fit w-fit p-0.5 hover:text-white")} onClick={() => handleRemoveAttachment(attachment_0.tempId)} type="button" disabled={attachment_0.isUploading}><X className="size-2.5" /></Button></motion.div>;
      $[8] = handleRemoveAttachment;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    t3 = attachments.map(t4);
    $[5] = attachments;
    $[6] = handleRemoveAttachment;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <AnimatePresence>{t3}</AnimatePresence>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t4) {
    t5 = <div className={t2}>{t4}</div>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function getFileType(contentType: string) {
  if (contentType.startsWith('image/')) {
    return 'image';
  }
  if (contentType === 'application/pdf') {
    return 'pdf';
  }
  return 'file';
}
function FilePreview(t0) {
  const $ = _c(20);
  const {
    attachment
  } = t0;
  const contentType = attachment.file.type || "";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FileText className="h-6 w-6 text-gray-600" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== contentType) {
    t2 = getFileType(contentType).toUpperCase();
    $[1] = contentType;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="flex h-full w-full flex-col items-center justify-center rounded-sm bg-gray-50">{t1}<span className="mt-1 text-xs font-medium text-gray-600">{t2}</span></div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let inner = t3;
  if (contentType.startsWith("image/")) {
    let t4;
    if ($[5] !== attachment.file) {
      t4 = URL.createObjectURL(attachment.file);
      $[5] = attachment.file;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    const t5 = attachment.file.name || "Attachment preview";
    let t6;
    if ($[7] !== t4 || $[8] !== t5) {
      t6 = <img src={t4} alt={t5} className="h-full w-full rounded-sm object-cover" />;
      $[7] = t4;
      $[8] = t5;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    inner = t6;
  }
  let t4;
  if ($[10] !== inner) {
    t4 = <TooltipTrigger type="button" className="size-full cursor-help">{inner}</TooltipTrigger>;
    $[10] = inner;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = attachment.isUploading && " (uploading...)";
  const t6 = attachment.uploadError && " (upload failed)";
  const t7 = attachment.fileUuid && !attachment.isUploading && " (uploaded)";
  let t8;
  if ($[12] !== attachment.file.name || $[13] !== t5 || $[14] !== t6 || $[15] !== t7) {
    t8 = <TooltipContent>{attachment.file.name}{t5}{t6}{t7}</TooltipContent>;
    $[12] = attachment.file.name;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t4 || $[18] !== t8) {
    t9 = <TooltipProvider delayDuration={0}><Tooltip>{t4}{t8}</Tooltip></TooltipProvider>;
    $[17] = t4;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}